import React, { Fragment, useEffect, useMemo, useState, useRef } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/fun-ui/Popover";
import { Col } from "@/stores/slices/paperSlice";
import { Button } from "@/components/fun-ui/Button";
import { Checkbox } from "@/components/ui/checkbox";
import { AITableIcon, DeleteIcon, HelpCircleIcon, LetterIcon } from "@/components/fun-ui/icons";
import { Consumptions } from "@/components/balances/Consumptions";
import { Tip } from "@/components/fun-ui/Tip";
import { CreateCustomAIColButton, CustomAIColForm } from "./CreateCustomAICol";
import { Alert, AlertTrigger } from "@/components/fun-ui/Alert";
import { useAiCol, useAiColsConfig, useUser, useUserVip } from "@/stores/appStore";
import { useSubjectId } from "@/components/subjects/hooks/usePathParams";
import { cn } from "@/lib/utils";
import { UserTriggeredDialog } from "@/components/guides/GuidesDialog";
import { AIColLimitFloatingCard, LimitType } from "./AIColLimitFloatingCard";
import { useFloatingCard } from "@/hooks/useFloatingCard";
import { flip, FloatingPortal, offset } from "@floating-ui/react";
import { chartColKeys, chartColTips, LANG_LIST } from "./lib/aiColsConst";

export default function AIColsSetter({
  cols = [],
  applyNewCols = () => { },
  iconOnly = false,
}: {
  cols: Col[];
  applyNewCols: (keys: string[], prev: string[], cols: Col[]) => void;
  iconOnly?: boolean;
}) {
  const [defaultColKeys, setDefaultColKeys] = useState<Record<string, boolean>>({});
  const [colKeys, setColKeys] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState<boolean>(false);
  const [limitType, setLimitType] = useState<LimitType>('custom');
  const {
    open: floatingOpen,
    refs,
    floatingStyles,
    getReferenceHoverProps,
    getFloatingHoverProps,
  } = useFloatingCard({
    placement: 'left-start',
    middleware: [
      offset(20),
      flip({ fallbackPlacements: ['right-start'] }),
    ],
  });

  const createBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const colKeys = cols.reduce((acc, { key, selected }) => {
      return {
        ...acc,
        [key]: selected,
      }
    }, {});
    setDefaultColKeys(colKeys);
    setColKeys(colKeys);
  }, [cols]);

  const {
    fetch: fetchAiColsConfig,
    value: aiColsConfig,
  } = useAiColsConfig();

  useEffect(() => {
    fetchAiColsConfig();
  }, [fetchAiColsConfig]);

  const totalCost = useMemo(() => {
    let total = 0;
    cols.map(({ key, cost }) => {
      if (!!colKeys[key]) {
        total += cost;
      }
    });
    return total;
  }, [colKeys, cols]);

  const allColsList = useMemo(() => [{
    title: '创建的 AI 表格列：',
    currentcols: cols.filter(({ isCustom }) => isCustom),
  }, {
    title: '智能解析表格列：',
    currentcols: cols.filter(({ key }) => chartColKeys.includes(key)),
  }, {
    title: '预设的 AI 表格列：',
    currentcols: cols.filter(({ isCustom, key }) => !isCustom && !chartColKeys.includes(key)),
  }], [cols]);

  const {
    deleteAiCol
  } = useAiCol();

  const subjectId = useSubjectId();

  useEffect(() => {
    if (!open) {
      setColKeys(defaultColKeys);
    }
  }, [defaultColKeys, open]);


  const {
    limits
  } = useUserVip();

  const isAiColsLimited = useMemo(() => {
    if (limits) {
      if (limits.aiColLimit < 0) {
        return false;
      } else {
        const counts = cols.filter(({ selected }) => selected).length;
        return counts >= limits.aiColLimit;
      }
    }
    return true;
  }, [cols, limits]);
  const triggerLabel = 'AI 表格列';

  if (!limits) {
    return (
      <></>
    )
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div>
            <Tip label={iconOnly ? triggerLabel : ''}>
              <div>
                <Button
                  variant="outline"
                  size='default'
                  className={cn(
                    "flex gap-1",
                    iconOnly ? "w-10 px-0" : "min-w-[130px]"
                  )}
                >
                  <AITableIcon className="h-4 w-4" />
                  <span className={cn(
                    iconOnly && "hidden"
                  )}>{triggerLabel}</span>
                </Button>
              </div>
            </Tip>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[320px] p-5"
        >
          <CustomAIColForm>
            <div className="text-sm text-muted-foreground flex items-center">
              <span>选择展示的列</span>
              <Tip
                className="ml-2 cursor-help"
                side="right"
                label={(
                  <ul className="ml-2 list-disc">
                    <li>每生成 1 个单元格
                      <ul className="ml-4 list-disc">
                        <li>创建的 AI 表格列消耗 {aiColsConfig?.customPrice} 个飞豆</li>
                        <li>预设的 AI 表格列消耗 {aiColsConfig?.presetPrice} 个飞豆</li>
                        <li>智能解析表格列消耗 {aiColsConfig?.chartPrice} 个飞豆</li>
                      </ul>
                    </li>
                    <li>生成过的单元格不再消耗飞豆</li>
                    <li>无法解析的文献不会消耗飞豆</li>
                  </ul>
                )}
              >
                <HelpCircleIcon />
              </Tip>
              {
                aiColsConfig?.letter?.url ? (
                  <UserTriggeredDialog
                    width={aiColsConfig?.letter?.width}
                    height={aiColsConfig?.letter?.height}
                    url={aiColsConfig?.letter.url}
                  >
                    <div className="max-w-fit flex items-center">
                      <Tip label={aiColsConfig.letter.tip}>
                        <LetterIcon className="ml-2 cursor-pointer" />
                      </Tip>
                    </div>
                  </UserTriggeredDialog>
                ) : null
              }
            </div>
            {allColsList[0].currentcols.length < limits.customLimit ? (
              <CreateCustomAIColButton className="mt-3" />
            ) : (
              <div
                ref={createBtnRef}
                className="mt-3 max-w-fit"
                {...getReferenceHoverProps(() => {
                  setLimitType('custom');
                })}
              >
                <CreateCustomAIColButton disabled />
              </div>
            )}
            <ul className="text-sm mb-5 max-h-[240px] overflow-auto">
              {
                allColsList.map(({ title, currentcols }, i) => {
                  return (
                    <Fragment key={title}>
                      {
                        currentcols.length ? (
                          <li className={
                            cn(
                              "text-sm text-muted-foreground py-2 mt-3"
                            )
                          }>{title}</li>
                        ) : null
                      }
                      {
                        currentcols.map((col) => {
                          const id = `ai_col_${col.key}`;
                          const lang = LANG_LIST.find(item => item.value === (col.lang || 'zh'))?.label;
                          const isChartCol = chartColKeys.includes(col.key);
                          return (
                            <li
                              className={cn(
                                "w-full flex items-center px-2 space-x-3 justify-between",
                              )}
                              key={col.key}
                            >
                              <div
                                className="flex items-center"
                              >
                                <label
                                  htmlFor={id}
                                  className="text-sm font-medium cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70 inline-flex items-center gap-2 py-2"
                                  {...(isAiColsLimited ? getReferenceHoverProps(() => {
                                    setLimitType('aicols');
                                  }) : {})}
                                >
                                  <Checkbox
                                    id={id}
                                    className="data-[state=unchecked]:border-foreground"
                                    checked={!!colKeys[col.key]}
                                    onCheckedChange={(checked) => {
                                      setColKeys({
                                        ...colKeys,
                                        [col.key]: !!checked,
                                      })
                                    }}
                                  />
                                  <span>{col.name}</span>
                                </label>
                                {
                                  col.isCustom ? (
                                    <>
                                      <Tip
                                        className="cursor-help ml-3 text-muted-foreground"
                                        side="top"
                                        label={`${lang}｜${col.desc}`}
                                      >
                                        <HelpCircleIcon />
                                      </Tip>
                                      <Alert
                                        className=""
                                        autoTrigger={false}
                                        title="删除 AI 表格列"
                                        description={
                                          <>
                                            <span className="block text-sm">确认删除 AI 表格列“{col.name}”吗？删除后该列数据将丢失且不可找回</span>
                                            <span className="block text-sm text-annotation-foreground">表格列输出语言：{lang}</span>
                                            <span className="block text-sm text-annotation-foreground">表格列提取要求：{col.desc}</span>
                                          </>
                                        }
                                        confirmLabel="删除"
                                        confirmVariant="destructive"
                                        asyncConfirm={async () => {
                                          await deleteAiCol({
                                            colId: col.colId,
                                          }, subjectId);
                                        }}
                                      >
                                        <AlertTrigger
                                          className="w-4 h-4 p-0 m-0 leading-none ml-3 text-muted-foreground"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                        >
                                          <DeleteIcon />
                                        </AlertTrigger>
                                      </Alert>
                                    </>
                                  ) : null
                                }
                                {
                                  isChartCol ? (
                                    <Tip
                                      className="cursor-help ml-3 text-muted-foreground"
                                      side="top"
                                      label={chartColTips[col.key as (keyof typeof chartColTips)]}
                                    >
                                      <HelpCircleIcon />
                                    </Tip>
                                  ) : null
                                }
                              </div>
                              <Consumptions className="mr-1" scene={{
                                label: col.cost ? `新生成 ${col.cellCount} 个单元格消耗 ${col.cost} 个飞豆` : '无需消耗飞豆',
                                price: col.cost,
                              }} />
                            </li>
                          );
                        })
                      }
                    </Fragment>
                  );
                })
              }
            </ul>

            <div className="flex justify-end space-x-2 mt-12">
              <Button
                onClick={() => {
                  setColKeys(defaultColKeys);
                  setOpen(false);
                }}
                variant="outline"
              >
                取消
              </Button>
              <div className="relative">
                <Consumptions className="absolute -translate-y-full right-1 -top-2" scene={{
                  label: totalCost ? `新生成 ${totalCost} 个单元格消耗 ${totalCost} 个飞豆` : '无需消耗飞豆',
                  price: totalCost,
                }} />
                <Button
                  disabled={
                    Object.keys(colKeys).filter(key => !!colKeys[key]).join(',') === Object.keys(defaultColKeys).filter(key => !!defaultColKeys[key]).join(',')
                  }
                  className="disabled:bg-accent disabled:text-foreground"
                  onClick={() => {
                    const selectedKeys = Object.keys(colKeys).filter(key => !!colKeys[key]);
                    applyNewCols(
                      selectedKeys,
                      Object.keys(defaultColKeys).filter(key => !!defaultColKeys[key]),
                      cols.filter(col => selectedKeys.includes(col.key)),
                    );
                    setOpen(false);
                  }}
                >
                  应用并更新表格内容
                </Button>
              </div>
            </div>
          </CustomAIColForm>
          <FloatingPortal>
            {floatingOpen && (
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, zIndex: 51 }}
                {...getFloatingHoverProps()}
              >
                <AIColLimitFloatingCard limitType={limitType} />
              </div>
            )}
          </FloatingPortal>
        </PopoverContent>
      </Popover>

    </>

  )
}